import axios from 'axios'
import {
  PublicClientApplication,
  BrowserAuthError,
  InteractionRequiredAuthError
} from '@azure/msal-browser'
import {LOG_OUT} from '../constants/actionTypes.js'
import {msalConfig, loginRequest} from '../../authConfig.js'

export const msalInstance = new PublicClientApplication(msalConfig)

export const logoutUser = (instance, dispatch) => {
  if (dispatch) {
    dispatch({type: LOG_OUT, isLoading: false, isAuthenticated: false})
  }
  instance.logoutRedirect({account: instance.getActiveAccount()})
}

// Function to get the access token safely using MSAL
export const getAccessToken = (instance) => {
  const accounts = instance?.getAllAccounts()
  if (accounts && accounts.length > 0) {
    return new Promise((resolve, reject) => {
      instance
        .acquireTokenSilent({
          ...loginRequest, // Spread to properly merge scopes/configurations
          account: accounts[0]
        })
        .then((response) => {
          resolve(response.accessToken)
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            console.log('Session expired. Redirecting to Microsoft Login...')

            // loginRedirect kills the page instance to navigate away.
            // Reject the promise immediately to prevent downstream Axios code from running.
            instance.loginRedirect(loginRequest).catch(console.error)
            reject(new Error('Interaction required: Redirecting to login...'))
          } else if (
            error instanceof BrowserAuthError &&
            error.errorCode === 'user_cancelled'
          ) {
            logoutUser(instance)
            reject(new Error('Sign-in process was cancelled by the user.'))
          } else {
            console.log('Error acquiring token silently:', error)
            reject(error)
          }
        })
    })
  }
  return Promise.reject(new Error('No user accounts available.'))
}

// Create an instance of Axios with a defensive request interceptor
export const createAPI = (instance) => {
  const baseURL = ''
  const API = axios.create({baseURL})

  let isRefreshed = false

  API.interceptors.request.use(
    async (req) => {
      try {
        const token = await getAccessToken(instance)
        if (token) {
          req.headers.Authorization = `Bearer ${token}`
        }
        return req
      } catch (error) {
        console.log('Interceptor caught token generation error:', error.message)

        // If a redirect was triggered above, prevent the request chain from looping
        if (error.message.includes('Interaction required')) {
          return Promise.reject(error)
        }

        if (!isRefreshed) {
          isRefreshed = true
          try {
            const accounts = instance.getAllAccounts()
            if (accounts.length > 0) {
              const newToken = await instance.acquireTokenSilent({
                account: accounts[0],
                scopes: loginRequest.scopes
              })
              req.headers.Authorization = `Bearer ${newToken.accessToken}`
              return axios(req)
            }
          } catch (refreshError) {
            console.log(
              'Error refreshing token in interceptor fallback:',
              refreshError
            )
          }
        }
        return Promise.reject(error)
      }
    },
    (err) => Promise.reject(err)
  )

  return API
}

export const API = createAPI(msalInstance)

export const verifyUser = async (instance) => {
  try {
    const {data} = await API.get('/api/v1/login')
    return data
  } catch (error) {
    console.log('Verify user failed:', error)

    // FIXED: Never call localStorage.clear(). Clean up only MSAL cache references natively.
    const activeAccount =
      instance.getActiveAccount() || instance.getAllAccounts()[0]

    if (activeAccount) {
      // Clear MSAL's temporary transaction states without destroying your bookmarks
      instance.logoutRedirect({account: activeAccount}).catch(console.error)
    } else {
      instance.loginRedirect(loginRequest).catch(console.error)
    }

    return {verified: false, status: 401}
  }
}
